<template>
  <div>
    <div
      class="client-service"
      :class="[
        hideDesktop ? 'client-service--hide-desktop' : '',
        hideMobile ? 'client-service--hide-mobile' : '',
        isWithinBusinessHours ? 'client-service--is-online' : '',
      ]"
    >
      <button
        ref="clientServiceButton"
        type="button"
        class="client-service__button"
        :class="[{ 'client-service__button--positioned': positionButton }]"
        @click="show"
      >
        <span
          class="text"
          v-text="$string('general.client_service.help')"
        />
        <span class="icon" />
      </button>
    </div>
    <overlay
      key="clientService"
      namespace="clientService"
      type="drawer"
      direction="void"
      block-class="client-service-overlay"
      :teleport="false"
    >
      <template #title>
        <div>
          <div
            class="client-service-overlay-title-text"
            v-text="$string('general.client_service.help')"
          />
          <div
            class="client-service-overlay-logo"
            v-html="$variable('design.logo_primary')"
          />
        </div>
      </template>
      <template #body>
        <div
          class="client-service-overlay__body-scroll"
          :class="[
            isWithinBusinessHours ? 'client-service--is-online' : '',
          ]"
          js-body-scroll-lock-ignore
        >
          <div class="client-service-menus">
            <div
              v-for="(item, index) of accordionItems"
              :key="`client-service-menu-${index}`"
              class="client-service-menu"
              :class="`client-service-menu-${item.handle}`"
            >
              <div class="client-service-menu__header">
                <span v-text="item.header" />
              </div>
              <ul class="client-service-menu__items">
                <li
                  v-for="(link, linkIndex) of item.menu"
                  :key="`client-service-menu-item-${linkIndex}`"
                >
                  <a
                    :href="link.link"
                    class="client-service-menu-link"
                    :class="[
                      link.badge
                        ? generateBadgeClasses(
                          'client-service-menu-link',
                          link.badge
                        )
                        : '',
                    ]"
                    @click="(event) => handleGtm(event)"
                  >
                    <span v-text="link.label" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <button
          type="button"
          @click="openChat"
          v-text="$string('general.client_service.button')"
        />
        <div class="client-service-overlay__footer-text">
          <p v-text="$string('general.client_service.footer')" />
        </div>
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Client service (client-service)
 * -----------------------------------------------------------------------------
 * Global modal for Client service.
 *
 * @param {Object} navigation - Menus to be displayed in the client service.
 * @param {Object} hideDesktop - Hide on desktop.
 * @param {Object} hideMobile - Hide on mobile.
 * @param {Object} businessHoursStartTime - Business hours start time.
 * @param {Object} businessHoursEndTime - Business hours end time.
 */
import { mapActions, mapGetters } from 'vuex'
import Overlay from '~global/overlay/overlay'
import { formatLinklist } from '~/helpers/format'

const dataLayer = window.dataLayer || []

export default {
  name: 'ClientService',

  components: {
    Overlay,
  },

  props: {
    hideDesktop: {
      type: Boolean,
      default: false,
    },
    hideMobile: {
      type: Boolean,
      default: false,
    },
    showText: {
      type: Boolean,
      default: true,
    },
    textDelay: {
      type: Number,
      default: 4,
    },
    textDuration: {
      type: Number,
      default: 3,
    },
    businessHoursStartTime: {
      type: Number,
      default: 8,
    },
    businessHoursEndTime: {
      type: Number,
      default: 12,
    },
    navigation: {
      type: Object,
      default: () => ({
        contact: '',
        help: '',
      }),
    },
  },

  emits: {
    'client-service-updated': () => true,
  },

  data() {
    return {
      isWithinBusinessHours: false,
      positionButton: false,
      accordionItems: [
        {
          header: this.$string('general.client_service.contact'),
          handle: 'contact',
          menu: this.navigation?.contact?.map((link) => formatLinklist(link)),
        },
        {
          header: this.$string('general.client_service.help'),
          handle: 'help',
          menu: this.navigation?.help?.map((link) => formatLinklist(link)),
        },
      ],
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
      getRelevantMessaging: 'multi-store/getRelevantMessaging',
    }),
  },

  mounted() {
    cnvs.EventBus.listen('site-footer-loaded', () => {
      this.toggleVisibility()
    })

    this.checkBusinessHours()
    setInterval(this.checkBusinessHours, 60000)

    if (this.showText && this.isWithinBusinessHours && !sessionStorage.getItem('clientServiceText')) {
      sessionStorage.setItem('clientServiceText', 'true')
      this.toggleTextVisibility(this.textDelay * 1000)
      this.toggleTextVisibility((this.textDelay + this.textDuration) * 1000)
    }

    window.setTimeout(() => {
      this.positionButton = window.scrollY < 595
    }, 500)

    window.addEventListener('scroll', () => {
      this.positionButton = window.scrollY < 595
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Open the overlay.
     */
    show() {
      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'clientService',
      })

      dataLayer.push({
        event: 'client_service_modal_opened',
      })
    },

    openChat() {
      try {
        window.zE('messenger', 'open')

        dataLayer.push({
          event: 'client_service_chat_started',
        })
      } catch (err) {
        // Do nothing
      }
    },

    /**
     * Check if an accordion item is active using its index.
     * @param {Number} index - Index of selected accordion item.
     * @returns {Boolean}
     */
    isActive(index) {
      return this.accordionItems[index].active
    },

    /**
     * Toggle accordion item active state.
     * @param {Number} currentIndex - Index of selected accordion item.
     */
    toggleAccordion(currentIndex) {
      if (this.isActive(currentIndex)) {
        this.accordionItems[currentIndex].active = false
        return
      }

      this.accordionItems.forEach((item, index) => {
        item.active = currentIndex === index
      })

      this.$emit('client-service-updated')
    },

    generateBadgeClasses(append = '', badgeString) {
      if (!badgeString) {
        return ''
      }

      // Split the badgeString by commas and trim any leading/trailing spaces
      const badgeClasses = badgeString
        .split(',')
        .map((classStr) => `${append}--${classStr.trim()}`)

      // Join the badgeClasses into a space-separated string
      return badgeClasses.join(' ')
    },

    toggleVisibility() {
      const siteFooterLogo = document.querySelector('.site-footer__logo')
      const clientServiceButton = this.$refs.clientServiceButton

      // hide clientServiceButton when site footer logo is visible
      if (siteFooterLogo) {
        const siteFooterLogoObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              clientServiceButton.classList.add('client-service__button--is-hidden')
            } else {
              clientServiceButton.classList.remove('client-service__button--is-hidden')
            }
          })
        })

        siteFooterLogoObserver.observe(siteFooterLogo)
      }
    },

    toggleTextVisibility(delay = 0) {
      const clientServiceButton = this.$refs.clientServiceButton

      setTimeout(() => {
        clientServiceButton.classList.toggle('client-service__button--show-text')
      }, delay)
    },

    checkBusinessHours() {
      const now = new Date().toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' })
      const time = new Date(now)
      const startBusinessHour = new Date(time)
      const endBusinessHour = new Date(time)

      startBusinessHour.setHours(this.businessHoursStartTime, 0, 0)
      endBusinessHour.setHours(this.businessHoursEndTime, 0, 0)

      this.isWithinBusinessHours = time >= startBusinessHour && time <= endBusinessHour
    },

    handleGtm(event) {
      dataLayer.push({
        event: 'contact_click',
        contactClick: {
          url: document.location.pathname,
          type: event.target.innerText,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import "./client-service";
</style>
